/**
 * /sources/$collectionId/import — once-only WhatsApp export backfill for a single group.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Check, ChevronLeft, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Dropzone } from '@/components/patterns/dropzone';
import { InfoNote } from '@/components/patterns/info-note';
import { PageHeader } from '@/components/patterns/page-header';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { connectorApi, type ImportAmbiguityError, importsApi } from '@/lib/api';
import { IANA_TIMEZONES } from '@/lib/constants';
import { qk } from '@/lib/query-client';
import { browserTimezone } from '@/lib/utils';

export function ImportPage() {
  const { collectionId } = useParams({ strict: false }) as { collectionId?: string };
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [timezone, setTimezone] = useState(browserTimezone());
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [ambiguity, setAmbiguity] = useState<ImportAmbiguityError | null>(null);

  const { data: groups, isLoading: groupsLoading } = useQuery({
    queryKey: qk.groups,
    queryFn: () => connectorApi.listGroups(),
  });
  const { data: importsData, isLoading: importsLoading } = useQuery({
    queryKey: qk.imports,
    queryFn: () => importsApi.list(),
  });

  const group = groups?.find((g) => g.id === collectionId);
  const doneBatch = importsData?.batches.find(
    (b) => b.collectionId === collectionId && b.status === 'done',
  );

  const upload = useMutation({
    mutationFn: ({ f, tz, dateOrder }: { f: File; tz: string; dateOrder?: 'dmy' | 'mdy' }) =>
      importsApi.upload(f, tz, '', dateOrder, collectionId),
    onSuccess: (res) => {
      if ('ambiguity' in res) {
        setAmbiguity(res.ambiguity ?? null);
        return;
      }
      setAmbiguity(null);
      void qc.invalidateQueries({ queryKey: qk.imports });
      toast.success('Import started — history will appear in Sources shortly.');
      void navigate({ to: '/sources' });
    },
    onError: (err) => {
      const msg = (err as { message?: string })?.message;
      toast.error(msg && msg.length > 3 ? msg : 'Upload failed. Please try again.');
    },
  });

  function handleFile(f: File) {
    if (!collectionId) return;
    const ok = f.name.toLowerCase().endsWith('.txt') || f.name.toLowerCase().endsWith('.zip');
    if (!ok) {
      toast.error('Only .txt and .zip files are supported');
      return;
    }
    setPendingFile(f);
    setAmbiguity(null);
    upload.mutate({ f, tz: timezone });
  }

  const groupName = group?.title ?? '…';
  const loading = groupsLoading || importsLoading;

  return (
    <div className="mx-auto max-w-[640px]">
      <Link
        to="/sources"
        className="inline-flex items-center gap-1 text-[13px] text-faint transition-colors hover:text-foreground"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
        Back to Sources
      </Link>

      <PageHeader
        className="mt-4"
        title="Import chat history"
        subtitle={`Backfill messages into ${groupName}. History can only be imported once per group.`}
      />

      <div className="mt-8 rounded-xl border border-border p-8">
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : doneBatch ? (
          <div className="flex items-start gap-3">
            <Check className="mt-0.5 h-5 w-5 shrink-0 text-success" />
            <div>
              <p className="text-[15px] font-semibold">Chat history already imported</p>
              <p className="mt-1 text-[13px] text-faint">
                {doneBatch.filename} · {(doneBatch.counts?.imported ?? 0).toLocaleString()} messages
                · {new Date(doneBatch.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            <p className="text-[14.5px] text-muted-foreground">
              Export this group&apos;s chat from WhatsApp (Settings → Chats → Export Chat) and
              upload the file below.
            </p>

            <div className="space-y-1.5">
              <Label htmlFor="import-tz">Timezone</Label>
              <Select id="import-tz" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                {IANA_TIMEZONES.length === 0 ? (
                  <option value={timezone}>{timezone}</option>
                ) : (
                  IANA_TIMEZONES.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))
                )}
              </Select>
              <InfoNote>
                WhatsApp exports don&apos;t include a timezone — pick the one the chat happened in,
                or messages may land on the wrong day.
              </InfoNote>
            </div>

            <Dropzone onFile={handleFile} disabled={upload.isPending} />

            {upload.isPending && (
              <div className="flex items-center gap-2 text-[13px] text-faint">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Uploading {pendingFile?.name}…
              </div>
            )}

            {ambiguity && (
              <div className="space-y-3 rounded-md border border-warning-border bg-warning-bg p-4">
                <p className="text-[13.5px] font-semibold">Date format ambiguous</p>
                <p className="text-[13px] text-faint">
                  Brain couldn&apos;t tell whether dates are day/month/year or month/day/year. Pick
                  the correct one:
                </p>
                <div className="space-y-2">
                  {(ambiguity.interpretations ?? []).map((interp) => (
                    <Button
                      key={interp.dateOrder}
                      variant="outline"
                      size="sm"
                      className="w-full justify-start"
                      disabled={upload.isPending || !pendingFile}
                      onClick={() => {
                        if (pendingFile) {
                          upload.mutate({
                            f: pendingFile,
                            tz: timezone,
                            dateOrder: interp.dateOrder,
                          });
                        }
                      }}
                    >
                      <span className="font-mono text-xs">
                        {interp.dateOrder === 'dmy' ? 'DD/MM/YYYY' : 'MM/DD/YYYY'}
                      </span>
                      <span className="ml-2 text-faint">— e.g. {interp.sample}</span>
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
